import { tv } from '../../tv';
import { cardStyles } from '../card/card';
import { spinnerStyles } from '../spinner/spinner';

/**
 * The row every popup list shares: a menu's item and a picker's option are
 * the same 32px row, so the recipe lives once here and Menu and ListBox
 * consume it by direct import (menu.md, picker.md). Internal to ComplyKit.
 *
 * Items: sm text on a 32px row (the product's existing menu-row height), a
 * step-2 radius inside the popup's step-3, the neutral alpha wash for
 * hover/focus/press (focus is the wash too, the list convention, not the
 * ring), and tones that recolour without reshaping (the Button precedent).
 */
export const rowStyles = tv({
  slots: {
    list: 'ck:p-1 ck:outline-none',
    item: [
      'ck:flex ck:items-center ck:gap-2',
      'ck:px-2',
      'ck:font-sans ck:text-sm',
      'ck:cursor-pointer ck:select-none ck:outline-none',
      // Real RAC disabled (no reason to show): washed and skipped by arrows.
      'ck:disabled:opacity-50 ck:disabled:cursor-not-allowed',
    ],
    itemIcon: 'ck:shrink-0',
    /* The checkmark column: the glyph inherits the item's colour, and an
     * unselected item keeps the column's width (invisible, not absent) so
     * labels align down the list whatever is checked. */
    itemCheck: 'ck:shrink-0',
    /* The choice mark of a card or a record row: a checkbox (a radio in a
     * single list) at its end, in place of the check column. A card has no
     * list around it to say it can be chosen, so the empty box says it
     * before anything is. */
    itemMark: 'ck:shrink-0 ck:flex ck:items-center ck:justify-center',
    itemLabel: 'ck:flex-1',
    /* The two-line form (description present): a text column seated beside
     * the glyph, the Atlassian pattern, so the description indents under the
     * label, never under the icon. The description states its muted colour
     * outright, so a critical tone recolours the label while the second line
     * stays secondary. */
    itemText: 'ck:flex-1 ck:flex ck:flex-col ck:min-w-0',
    itemDescription: 'ck:text-xs ck:text-text-muted',
    /* The record form (picker.md): the name and its status on the first
     * line, then the description, then the tags, each a line of its own. One
     * wrapping row rather than a column, so the status can sit beside the
     * name while staying inside the description slot (recordDetails takes no
     * box), and the option is described by state, description and tags. */
    recordText: [
      'ck:flex-1 ck:min-w-0',
      'ck:flex ck:flex-wrap ck:items-center ck:gap-x-2 ck:gap-y-1',
    ],
    recordName: 'ck:flex-1 ck:min-w-0 ck:truncate ck:font-medium',
    recordDetails: 'ck:contents',
    recordDescription: 'ck:basis-full ck:min-w-0 ck:truncate ck:text-xs ck:text-text-muted',
    recordTags: 'ck:basis-full ck:min-w-0 ck:flex ck:gap-1 ck:overflow-hidden',
    /* The trailing pending seat: ButtonSpinner's overlay slot, laid out as a
     * trailing cell rather than an absolute cover. */
    spinnerSeat: 'ck:shrink-0 ck:flex ck:items-center',
    spinnerDisc: spinnerStyles({ size: 'sm' }),
    /* The section heading: xs and muted so it reads as a label over the
     * options, never as an option (React Aria keeps it out of the arrow-key
     * order; the look just needs to agree). */
    sectionHeading: 'ck:px-2 ck:py-1 ck:text-xs ck:font-medium ck:text-text-muted ck:select-none',
  },
  variants: {
    selected: {
      true: {},
      false: { itemCheck: 'ck:invisible' },
    },
    /* Tones recolour, never reshape: critical moves the label and glyph onto
     * the error scale and the interaction wash onto the red alpha steps.
     * Press settles on the hover wash, and the explicit pressed: classes are
     * what give touch, where hover never fires, its press feedback. */
    tone: {
      neutral: { item: 'ck:text-text-default' },
      critical: { item: 'ck:text-error-text' },
    },
    /* The unavailable form (isDisabled + disabledReason): the washed disabled
     * LOOK on an item React Aria still treats as enabled, so hover and arrow
     * keys can reach it and the reason tooltip can open. The wash keeps the
     * focus/hover background so a keyboard user can see where the arrows are. */
    unavailable: {
      true: { item: 'ck:opacity-50 ck:cursor-not-allowed' },
      false: {},
    },
    pending: {
      true: { item: 'ck:cursor-default' },
      false: {},
    },
    /* A list that virtualizes needs one height per row: the label truncates
     * rather than wrapping (Menu's rows wrap, a popup list's do not). */
    truncate: {
      true: { itemLabel: 'ck:truncate', itemDescription: 'ck:truncate' },
      false: {},
    },
    /* The row's shape: a line is the 20px line, a 24px Avatar beside it, a
     * record three lines, its glyphs seated in a 20px box on the name's line
     * rather than centred on the whole row. The vertical inset is the
     * surface's (compounds below). */
    kind: {
      line: { itemIcon: 'ck:size-4', itemCheck: 'ck:size-4', itemMark: 'ck:size-4' },
      avatar: { itemIcon: 'ck:size-4', itemCheck: 'ck:size-4', itemMark: 'ck:size-4' },
      record: {
        item: 'ck:items-start',
        itemIcon: 'ck:h-5 ck:w-4',
        itemCheck: 'ck:h-5 ck:w-4',
        itemMark: 'ck:h-5 ck:w-4',
      },
    },
    /* Where the row sits. A row is the list's own: the step-2 radius inside
     * the popup's step-3, the wash its only edge. A card is a standalone
     * list's record on Card's outlined surface, answering the pointer and
     * the keyboard alike with an actionable Card's edge, and the
     * accent edge and tint once chosen; the group is what the choice mark's
     * states read. (Radius and padding live only here: tailwind-merge does
     * not know their tokens, so a base value beside the card's would both
     * apply.) */
    surface: {
      row: { item: 'ck:rounded-2' },
      card: {
        item: [
          cardStyles({ variant: 'outlined' }),
          'ck:group ck:p-3 ck:transition',
          // No lift: the scrolling list's edge would clip a shadow drawn outside the card.
          'ck:hover:border-border-interactive ck:focus-visible:border-border-interactive',
          // Step 3, the chosen chip's tint: one selection tint across the system.
          'ck:selected:border-border-interactive ck:selected:bg-accent-a-3',
        ],
      },
    },
  },
  /* A row's inset is 6px around the 20px line (the 32px row), 4px around a
   * 24px Avatar, 8px around a record; a card keeps its own 12px. The wash
   * is a row's too: a card answers with its edge instead. */
  compoundVariants: [
    { surface: 'row', kind: 'line', class: { item: 'ck:py-item' } },
    { surface: 'row', kind: 'avatar', class: { item: 'ck:py-1' } },
    /* A record row carries its choice mark at its end, the cards' checkbox
     * (picker.md): a check column held empty down a list of records reads
     * as a missing glyph. The group is what the mark's states read. */
    { surface: 'row', kind: 'record', class: { item: 'ck:group ck:py-2' } },
    {
      surface: 'row',
      tone: 'neutral',
      class: { item: 'ck:hover:bg-neutral-a-3 ck:focus:bg-neutral-a-3 ck:pressed:bg-neutral-a-3' },
    },
    {
      surface: 'row',
      tone: 'critical',
      class: { item: 'ck:hover:bg-red-a-3 ck:focus:bg-red-a-3 ck:pressed:bg-red-a-3' },
    },
  ],
  defaultVariants: {
    surface: 'row',
    selected: false,
    tone: 'neutral',
    unavailable: false,
    pending: false,
    truncate: false,
    kind: 'line',
  },
});
